/**
 * Catalog identity capture — popup timing, dismiss persistence, recently viewed,
 * and email_captured dataLayer push after native Shopify customer form success.
 *
 * Popup timing (not per-page):
 *   - Session-accumulated delay (theme setting; default 40s wall-clock)
 *   - Desktop exit intent after a short session minimum (~12s)
 *   - Soft offer after closing a non-empty cart drawer (once per session)
 *   - Never opens while the cart drawer is active; defers until close
 *
 * Test mode: ?catalog_identity_test=1 forces the popup (ignores dismiss / delay).
 * Clear popup dismiss: localStorage.removeItem('catalog_identity_popup_dismissed')
 * Clear recently viewed: localStorage.removeItem('catalog_recently_viewed')
 * Reset session timer: sessionStorage.removeItem('catalog_identity_session_started_at')
 */
(() => {
  const STORAGE_POPUP = 'catalog_identity_popup_dismissed';
  const STORAGE_CAPTURED = 'catalog_identity_email_captured';
  const STORAGE_RECENT = 'catalog_recently_viewed';
  const STORAGE_SESSION_START = 'catalog_identity_session_started_at';
  const STORAGE_CART_CLOSE_OFFERED = 'catalog_identity_cart_close_offered';
  const MAX_RECENT = 12;
  const EXIT_INTENT_MIN_MS = 12000;
  const CART_CLOSE_SETTLE_MS = 700;

  const params = new URLSearchParams(window.location.search);
  const testMode = params.get('catalog_identity_test') === '1';
  const posted = params.get('customer_posted') === 'true';
  const identitySource = params.get('identity_source') || '';

  function pushDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function markCaptured(source) {
    try {
      localStorage.setItem(STORAGE_CAPTURED, '1');
      localStorage.setItem(STORAGE_POPUP, '1');
    } catch (_error) {
      /* ignore quota / private mode */
    }

    pushDataLayer({
      event: 'email_captured',
      identity_source: source || 'unknown',
    });
  }

  if (posted && identitySource) {
    markCaptured(identitySource);
  }

  // Kept for any future non-navigation capture hooks; native POST uses ?customer_posted=.
  document.addEventListener('catalog:email_captured', (event) => {
    const source = event?.detail?.source || identitySource || 'unknown';
    markCaptured(source);
  });

  function wasCaptured() {
    try {
      return localStorage.getItem(STORAGE_CAPTURED) === '1';
    } catch (_error) {
      return false;
    }
  }

  function wasPopupDismissed() {
    try {
      return localStorage.getItem(STORAGE_POPUP) === '1';
    } catch (_error) {
      return false;
    }
  }

  function dismissPopup() {
    try {
      localStorage.setItem(STORAGE_POPUP, '1');
    } catch (_error) {
      /* ignore */
    }
  }

  function getSessionStartedAt() {
    try {
      const raw = sessionStorage.getItem(STORAGE_SESSION_START);
      const existing = raw ? Number(raw) : NaN;
      if (Number.isFinite(existing) && existing > 0) return existing;
      const now = Date.now();
      sessionStorage.setItem(STORAGE_SESSION_START, String(now));
      return now;
    } catch (_error) {
      return Date.now();
    }
  }

  function sessionElapsedMs() {
    return Math.max(0, Date.now() - getSessionStartedAt());
  }

  function wasCartCloseOffered() {
    try {
      return sessionStorage.getItem(STORAGE_CART_CLOSE_OFFERED) === '1';
    } catch (_error) {
      return false;
    }
  }

  function markCartCloseOffered() {
    try {
      sessionStorage.setItem(STORAGE_CART_CLOSE_OFFERED, '1');
    } catch (_error) {
      /* ignore */
    }
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function isCartDrawerOpen() {
    const drawer = getCartDrawer();
    return drawer instanceof HTMLElement && drawer.classList.contains('active');
  }

  function isCartEmpty() {
    const drawer = getCartDrawer();
    if (!(drawer instanceof HTMLElement)) return true;
    return drawer.classList.contains('is-empty');
  }

  function initPopup() {
    const root = document.querySelector('[data-catalog-identity-popup]');
    if (!(root instanceof HTMLElement)) return;

    const hasSuccessMarkup = !!root.querySelector('[data-catalog-newsletter-success]');
    const isSuccessReturn =
      root.getAttribute('data-catalog-identity-popup-success') === '1' ||
      (posted && (identitySource === 'popup' || identitySource === '')) ||
      hasSuccessMarkup;

    if (isSuccessReturn) {
      root.classList.add('catalog-identity-popup--success');
      root.hidden = false;
      document.documentElement.classList.add('catalog-identity-popup-open');

      const success = root.querySelector('[data-catalog-newsletter-success]');
      if (success instanceof HTMLElement) {
        success.focus({ preventScroll: true });
      }

      pushDataLayer({ event: 'identity_popup_success_shown' });

      const closeSuccess = () => {
        root.hidden = true;
        document.documentElement.classList.remove('catalog-identity-popup-open');
        dismissPopup();
      };

      root.querySelectorAll('[data-catalog-identity-popup-dismiss]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          closeSuccess();
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !root.hidden) closeSuccess();
      });

      const successHoldMs = window.matchMedia('(max-width: 749px)').matches ? 12000 : 8000;
      window.setTimeout(() => {
        if (root.hidden) return;
        closeSuccess();
      }, successHoldMs);
      return;
    }

    if (!testMode && (wasCaptured() || wasPopupDismissed())) return;

    const delayMs = testMode ? 0 : Number(root.dataset.delay || 40000);
    let opened = false;
    let timerId = 0;
    let pendingTrigger = '';
    let checkoutNavigating = false;

    const open = (trigger) => {
      if (opened) return;
      if (!testMode && (wasCaptured() || wasPopupDismissed())) return;
      if (checkoutNavigating) return;

      if (isCartDrawerOpen()) {
        pendingTrigger = trigger || pendingTrigger || 'session';
        return;
      }

      opened = true;
      pendingTrigger = '';
      window.clearTimeout(timerId);
      root.hidden = false;
      document.documentElement.classList.add('catalog-identity-popup-open');

      // Form starts in a hidden dialog — wire CAPTCHA once it becomes visible
      // (Shopify's default focus wire-up can't see hidden fields). Do not
      // pre-protect every page form on load; that makes hCaptcha aggressive.
      const form = root.querySelector('[data-catalog-newsletter-form]');
      const captchaProtect = window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect;
      if (form instanceof HTMLFormElement && typeof captchaProtect === 'function') {
        captchaProtect(form);
      }

      const emailInput = root.querySelector('input[type="email"]');
      if (emailInput instanceof HTMLElement) {
        emailInput.focus({ preventScroll: true });
      } else {
        const closeBtn = root.querySelector('[data-catalog-identity-popup-dismiss]');
        if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });
      }
      pushDataLayer({
        event: 'identity_popup_opened',
        popup_trigger: trigger || 'session',
      });
    };

    const close = () => {
      root.hidden = true;
      document.documentElement.classList.remove('catalog-identity-popup-open');
      dismissPopup();
      pushDataLayer({ event: 'identity_popup_dismissed' });
    };

    root.querySelectorAll('[data-catalog-identity-popup-dismiss]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !root.hidden) close();
    });

    if (testMode) {
      open('test');
      return;
    }

    getSessionStartedAt();
    const elapsed = sessionElapsedMs();
    const remainingMs = Math.max(0, delayMs - elapsed);
    // If the session threshold already passed on a prior page, wait briefly
    // after paint so the popup does not flash during navigation.
    const openAfterMs = remainingMs === 0 && elapsed > 0 ? 1500 : remainingMs;
    timerId = window.setTimeout(() => open('session'), openAfterMs);

    const onExitIntent = (event) => {
      if (event.clientY > 0) return;
      if (sessionElapsedMs() < EXIT_INTENT_MIN_MS) return;
      if (isCartDrawerOpen()) return;
      window.clearTimeout(timerId);
      open('exit_intent');
      document.removeEventListener('mouseout', onExitIntent);
    };
    document.addEventListener('mouseout', onExitIntent);

    // Delegate — cart drawer section HTML is re-rendered after ATC.
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('#CartDrawer-Checkout')) return;
      checkoutNavigating = true;
      pendingTrigger = '';
    });

    document.addEventListener('cart-drawer:close', () => {
      if (opened || checkoutNavigating) return;
      if (wasCaptured() || wasPopupDismissed()) return;

      if (pendingTrigger) {
        const trigger = pendingTrigger;
        pendingTrigger = '';
        window.setTimeout(() => open(trigger), CART_CLOSE_SETTLE_MS);
        return;
      }

      if (wasCartCloseOffered() || isCartEmpty()) return;
      markCartCloseOffered();
      window.setTimeout(() => open('cart_close'), CART_CLOSE_SETTLE_MS);
    });
  }

  function readRecent() {
    try {
      const raw = localStorage.getItem(STORAGE_RECENT);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  function writeRecent(items) {
    try {
      localStorage.setItem(STORAGE_RECENT, JSON.stringify(items.slice(0, MAX_RECENT)));
    } catch (_error) {
      /* ignore */
    }
  }

  function trackProduct(root) {
    const id = root.dataset.trackProduct;
    if (!id) return;

    const entry = {
      id: String(id),
      handle: root.dataset.trackHandle || '',
      url: root.dataset.trackUrl || '',
      title: root.dataset.trackTitle || '',
      vendor: root.dataset.trackVendor || '',
      price: root.dataset.trackPrice || '',
      image: root.dataset.trackImage || '',
      imageAlt: root.dataset.trackImageAlt || '',
      viewedAt: Date.now(),
    };

    const next = readRecent().filter((item) => item && String(item.id) !== entry.id);
    next.unshift(entry);
    writeRecent(next);
  }

  function renderRecent(root) {
    const grid = root.querySelector('[data-catalog-recently-viewed-grid]');
    if (!(grid instanceof HTMLElement)) return;

    const limit = Number(root.dataset.limit || 4);
    const currentId = root.dataset.trackProduct ? String(root.dataset.trackProduct) : '';
    const items = readRecent()
      .filter((item) => item && item.url && String(item.id) !== currentId)
      .slice(0, limit);

    if (!items.length) {
      root.hidden = true;
      return;
    }

    grid.replaceChildren(
      ...items.map((item) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'catalog-recently-viewed__card';
        link.href = item.url;

        if (item.image) {
          const media = document.createElement('span');
          media.className = 'catalog-recently-viewed__media';
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = item.imageAlt || item.title || '';
          img.loading = 'lazy';
          img.width = 600;
          img.height = 600;
          media.appendChild(img);
          link.appendChild(media);
        }

        if (item.vendor) {
          const vendor = document.createElement('p');
          vendor.className = 'catalog-recently-viewed__vendor';
          vendor.textContent = item.vendor;
          link.appendChild(vendor);
        }

        const title = document.createElement('p');
        title.className = 'catalog-recently-viewed__title';
        title.textContent = item.title || '';
        link.appendChild(title);

        if (item.price) {
          const price = document.createElement('p');
          price.className = 'catalog-recently-viewed__price';
          price.textContent = item.price;
          link.appendChild(price);
        }

        li.appendChild(link);
        return li;
      })
    );

    root.hidden = false;
  }

  function initRecentlyViewed() {
    document.querySelectorAll('[data-catalog-recently-viewed]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      if (root.dataset.trackProduct) trackProduct(root);
      renderRecent(root);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initPopup();
    initRecentlyViewed();
  });
})();
